'use client'

/**
 * A page (or a panel) that takes files: drop one anywhere, or paste an image.
 *
 * Lifted from the note page's own intake (#14562) for #16101, so Home's
 * page-wide drop zone and the docked AI Chat panel use the SAME drag counting
 * and paste rule rather than a fork. The note page keeps its behaviour
 * exactly through `@/components/notes/use-note-page-file-intake`, which binds
 * this to its marker attribute.
 *
 *   - **Drop (window)** — {@link usePageFileIntake}: while files are dragged
 *     over the window, `dragging` is true and the page draws its overlay; a
 *     drop anywhere hands them to `onFiles`. A drag carrying text or a link is
 *     ignored, `dragover` is prevented so the browser never opens the file,
 *     and a drop something else already took (`defaultPrevented`) is left
 *     alone.
 *   - **Drop (element)** — {@link useDropTargetFileIntake}: the same counting
 *     for ONE element (the docked chat panel), whose handlers also stop
 *     propagation — a file dropped on the panel over the note page goes to
 *     chat, and the note page's window listeners never see the drag at all.
 *   - **Paste** — an image on the clipboard becomes a `pasted-image-…` file,
 *     named as the feedback modal names one. A paste into a text field that
 *     also carries text is the field's (copying from a document puts both on
 *     the clipboard), and so is any paste a handler already took, and any
 *     paste into a field outside the page's own content (marked by
 *     `pageAttribute`).
 *
 * Window-level listeners, registered only while `enabled`.
 */

import {
  type DragEvent as ReactDragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

type FilesVia = 'drop' | 'paste'

/** A drag carrying files, not text or a link dragged out of the page. */
export function carriesFiles(event: {
  dataTransfer: DataTransfer | null
}): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.closest('[contenteditable="true"]') !== null
  )
}

/** `pasted-image-2026-10-20T15-04-05.png`, as the feedback modal names one. */
export function pastedImageName(type: string, at: Date): string {
  const ext = type.split('/')[1] ?? 'png'
  const stamp = at.toISOString().replace(/[:.]/g, '-').slice(0, 19)
  return `pasted-image-${stamp}.${ext}`
}

/** Every image on the clipboard, named; text and other files are skipped. */
export function clipboardImages(
  clipboard: DataTransfer | null,
  at: Date = new Date(),
): File[] {
  if (clipboard === null) return []
  const images: File[] = []
  for (const item of Array.from(clipboard.items ?? [])) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue
    const blob = item.getAsFile()
    if (blob === null) continue
    images.push(
      new File([blob], pastedImageName(blob.type, at), { type: blob.type }),
    )
  }
  return images
}

/**
 * The images a paste should add to the page marked by `pageAttribute`, or
 * none when the paste belongs to the field it landed in (see the header).
 */
export function pastedImages(
  clipboard: DataTransfer | null,
  target: EventTarget | null,
  pageAttribute: string,
  at: Date = new Date(),
): File[] {
  if (clipboard === null) return []
  if (isEditable(target)) {
    if (clipboard.getData('text/plain') !== '') return []
    // A field beside the page (AI Chat, a dialog) is not the page's.
    if (!(target as HTMLElement).closest(`[${pageAttribute}]`)) return []
  }
  return clipboardImages(clipboard, at)
}

export function usePageFileIntake({
  enabled,
  onFiles,
  pageAttribute,
}: {
  enabled: boolean
  onFiles: (files: File[], via: FilesVia) => void
  /** The attribute marking the page's own content, e.g. `data-note-page`. */
  pageAttribute: string
}): { dragging: boolean } {
  const [dragging, setDragging] = useState(false)
  const latest = useRef(onFiles)
  latest.current = onFiles

  useEffect(() => {
    if (!enabled) return
    // dragenter / dragleave fire for every element crossed: count them, so
    // moving between children does not flicker the overlay.
    let depth = 0
    const reset = () => {
      depth = 0
      setDragging(false)
    }
    const onDragEnter = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      depth += 1
      setDragging(true)
    }
    const onDragLeave = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      depth = Math.max(0, depth - 1)
      if (depth === 0) setDragging(false)
    }
    const onDragOver = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      // Without this the browser opens the file instead of dropping it.
      event.preventDefault()
    }
    const onDrop = (event: DragEvent) => {
      if (!carriesFiles(event)) return
      reset()
      if (event.defaultPrevented) return
      event.preventDefault()
      const files = Array.from(event.dataTransfer?.files ?? [])
      if (files.length > 0) latest.current(files, 'drop')
    }
    const onPaste = (event: ClipboardEvent) => {
      if (event.defaultPrevented) return
      const images = pastedImages(
        event.clipboardData,
        event.target,
        pageAttribute,
      )
      if (images.length === 0) return
      event.preventDefault()
      latest.current(images, 'paste')
    }

    window.addEventListener('dragenter', onDragEnter)
    window.addEventListener('dragleave', onDragLeave)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('drop', onDrop)
    window.addEventListener('dragend', reset)
    window.addEventListener('paste', onPaste)
    return () => {
      window.removeEventListener('dragenter', onDragEnter)
      window.removeEventListener('dragleave', onDragLeave)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('drop', onDrop)
      window.removeEventListener('dragend', reset)
      window.removeEventListener('paste', onPaste)
      setDragging(false)
    }
  }, [enabled, pageAttribute])

  return { dragging }
}

export interface DropTargetHandlers {
  onDragEnter: (event: ReactDragEvent<HTMLElement>) => void
  onDragLeave: (event: ReactDragEvent<HTMLElement>) => void
  onDragOver: (event: ReactDragEvent<HTMLElement>) => void
  onDrop: (event: ReactDragEvent<HTMLElement>) => void
}

/**
 * The element-scoped sibling of {@link usePageFileIntake}: spread `handlers`
 * on the element. Every file drag over it is CLAIMED — propagation stops, so
 * a page-level intake beneath (the note page's) neither counts the drag nor
 * receives the drop. Disabled, the handlers do nothing and claim nothing.
 */
export function useDropTargetFileIntake({
  enabled,
  onFiles,
}: {
  enabled: boolean
  onFiles: (files: File[]) => void
}): { dragging: boolean; handlers: DropTargetHandlers } {
  const [dragging, setDragging] = useState(false)
  const depth = useRef(0)
  const latest = useRef(onFiles)
  latest.current = onFiles

  useEffect(() => {
    if (!enabled) {
      depth.current = 0
      setDragging(false)
    }
  }, [enabled])

  const claim = useCallback(
    (event: ReactDragEvent<HTMLElement>): boolean => {
      if (!enabled || !carriesFiles(event)) return false
      event.preventDefault()
      event.stopPropagation()
      return true
    },
    [enabled],
  )

  const handlers: DropTargetHandlers = {
    onDragEnter: event => {
      if (!claim(event)) return
      depth.current += 1
      setDragging(true)
    },
    onDragLeave: event => {
      if (!claim(event)) return
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) setDragging(false)
    },
    onDragOver: event => {
      claim(event)
    },
    onDrop: event => {
      if (!claim(event)) return
      depth.current = 0
      setDragging(false)
      const files = Array.from(event.dataTransfer?.files ?? [])
      if (files.length > 0) latest.current(files)
    },
  }

  return { dragging, handlers }
}
